import { useCallback } from 'react'
import { useOrgParams } from '~/common/use-org-params';
import { supabaseGetter } from "~/api/supabase-getter"
import { useCreateOrganization, useDeleteOrganization, useCreatePolygon } from "~/api/supabase-mutations"
import { useCurrentUser } from '~/common/use-current-user';
import { plotAdded, useCropsContextDispatch } from '~/common/context/crops/crops-context';
import { useMapRefresh } from '~/map/context/use-map-refresh';

export const useCreateNewPlot = ({
  name,
  points,
}) => {
  const { farmId } = useOrgParams()
  const { currentUser } = useCurrentUser()
  const { createOrganization } = useCreateOrganization()
  const { deleteOrganization } = useDeleteOrganization()
  const { createPolygon } = useCreatePolygon()
  const { triggerRefresh } = useMapRefresh()
  const cropsDispatch = useCropsContextDispatch()

  const createNewPlot = useCallback(async () => {
    console.log("createNewPlot called:", {
      name,
      points,
      parentOrgId: farmId,
    })

    let newPlotId

    try {
      const { data: plot, error: plotError } = await createOrganization({
        name,
        organization_type: 'plot',
        parent_organization_id: farmId,
        created_by: currentUser.id,
      })
      if (plotError) throw plotError
      newPlotId = plot.id

      const { error: polygonError } = await createPolygon({
        points,
        organization_id: newPlotId,
      })
      if (polygonError) throw polygonError

      // Invalidate caches and notify listeners to refetch
      // OrganizationPolygons and organizations are used by timeline and dashboard views
      supabaseGetter.organizationPolygons.invalidate()
      supabaseGetter.organizationUsers.invalidate()
      supabaseGetter.organizations.invalidate()
      supabaseGetter.crops.invalidate()
      supabaseGetter.cropPhases.invalidate()
      supabaseGetter.cropSchedules.invalidate()
      supabaseGetter.cropDetails.invalidate()
      supabaseGetter.tasks.invalidate()
      cropsDispatch(plotAdded({ plotId: newPlotId }))

      // Trigger map refresh to update visible organization polygons
      triggerRefresh()

      // return for crop assignment handling
      return newPlotId

    } catch (error) {
      console.error('createNewPlot failed, rolling back inserts:', error)

      // OrganizationPolygons don't cascade-delete from organizations, so deleting the org
      // is sufficient (polygon has organization_id FK but no cascade — orphan is
      // avoided because the polygon insert failed before we get here)
      if (newPlotId) {
        await deleteOrganization(newPlotId)
        supabaseGetter.organizations.invalidate()
      }

      throw error
    }
  }, [name, points, farmId, createOrganization, currentUser, createPolygon, cropsDispatch, triggerRefresh, deleteOrganization])

  return { createNewPlot }
}